// =============================================================================
// ui/media/useMediaObjectUrl.ts — one Storage object → one object URL.
// -----------------------------------------------------------------------------
// The whole of the "signed Storage URLs" work the three surfaces were waiting
// on — and it turns out beta never used a signed URL at all. Its `readBinary`
// downloads the blob through the authenticated client and calls
// `URL.createObjectURL` on it (index.html:23037, :23100, :11516). That is the
// same trip, minus a signing round-trip and minus a URL that expires mid-view.
//
// ⚠ So the copy on three surfaces saying "needs signed Storage URLs" was wrong
// about the mechanism as well as being out of date. It is deleted, not amended.
//
// ── 🚨 REVOKE, ALWAYS ──
//
// An object URL pins its blob for the lifetime of the document. A journal day
// with six chart captures is tens of megabytes, and stepping ‹ › through a month
// without revoking is a leak that ends as a tab crash. Every path here revokes:
// unmount, filename change, and the race-loser below.
//
// ── 🚨 THE RACE, WHICH IS REAL AND NOT THEORETICAL ──
//
// Stepping the journal date fires a new download while the previous one is in
// flight. Without the `alive` flag the slower response wins and paints the
// PREVIOUS day's chart under the current day's heading — a wrong picture that
// looks entirely correct. The flag also revokes the loser's URL rather than
// leaking it.
// =============================================================================

import { useEffect, useState } from 'react';
import { useSupabase } from '../state/supabase';
import { downloadMedia } from '../../io/storage/media';
import type { MediaBucket } from '../../io/storage/media';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface MediaObjectUrl {
  url: string | null;
  /** A human sentence, or null. Never a raw Supabase error. */
  error: string | null;
  loading: boolean;
}

/**
 * `null` filename is a legitimate idle state, not an error — the lightbox
 * renders before it has an index on the first paint.
 */
export function useMediaObjectUrl(bucket: MediaBucket, filename: string | null): MediaObjectUrl {
  const { sb } = useSupabase();
  const [state, setState] = useState<MediaObjectUrl>({ url: null, error: null, loading: !!filename });

  useEffect(() => {
    if (!filename) { setState({ url: null, error: null, loading: false }); return; }

    let alive = true;
    let created: string | null = null;
    setState({ url: null, error: null, loading: true });

    void (async () => {
      try {
        const blob = await downloadMedia(sb, bucket, filename);
        // ⚠ NULL IS "GONE", NOT "BROKEN" — `downloadMedia`'s own contract. A row
        // can outlive its object, and these buckets have no backup, so this is a
        // state the user will actually meet. It gets a sentence, not a throw —
        // and deliberately not an error-log entry either, unlike the catch below.
        // A missing object is an expected outcome; filling the activity log with
        // one line per dead thumbnail would bury the failures worth reading.
        if (!blob) {
          if (alive) setState({ url: null, loading: false, error: `${filename} is no longer in storage.` });
          return;
        }
        if (typeof URL.createObjectURL !== 'function') {
          if (alive) setState({ url: null, loading: false, error: 'This browser cannot display attachments.' });
          return;
        }
        created = URL.createObjectURL(blob);
        if (!alive) { URL.revokeObjectURL(created); return; }
        setState({ url: created, error: null, loading: false });
      } catch (e) {
        if (alive) {
          setState({
            url: null, loading: false,
            error: reportFailure(`load ${bucket}`, e, `Could not load ${filename}.`),
          });
        }
      }
    })();

    return () => {
      alive = false;
      if (created) URL.revokeObjectURL(created);
    };
  }, [sb, bucket, filename]);

  return state;
}
